import React, { useState } from "react";
import {
  Grid,
  Autocomplete,
  TextField,
  Paper,
  Dialog,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
} from "@mui/material";
import { useApi } from "@platform/api";
import { Alert, Button, Typography } from "@platform/design-system";
import {
  CARRIERS,
  COVERAGE_VALUES,
  COVERAGE_FORM,
  COVERAGE_NAMES,
  modalInitValues,
  MAIN_CARRIERS,
  ERROR_MESSAGES,
  ANTHEM_CARRIERS,
  CARRIER_GROUP_NUMBER_VALIDATION_PATTERN,
} from "@platform/rfp-core";

import {
  formatDate,
  isValidDate,
  normalizeClientProducts,
  extractErrorMessage,
  isValidAnnualPremium,
} from "../../utils";
import InputDate from "../inputDate";
import { submitEvent } from "../../utils/tracking";

import { STD_COV_SUBTYPES } from "./constants";

const {
  COVERAGE,
  SUBTYPES,
  NEW_CARRIER,
  CARRIER,
  EFFECTIVE_DATE,
  RENEWAL_DATE,
  PREMIUM,
  GROUP_NUMBER,
} = COVERAGE_VALUES;

const { PPO, HMO, INDEMNITY } = COVERAGE_FORM;

const { PRINCIPAL, PRINCIPAL_FINANCIAL, GUARDIAN } = MAIN_CARRIERS;

const {
  ANTHEM_VALIDATION,
  PRINCIPAL_VALIDATION,
  GUARDIAN_VALIDATION,
  PREMIUM_VALIDATION,
} = ERROR_MESSAGES;

const validations = {
  [RENEWAL_DATE]: (state) =>
    state[EFFECTIVE_DATE] &&
    state[RENEWAL_DATE] &&
    isValidDate(state[RENEWAL_DATE]) &&
    state[EFFECTIVE_DATE] < state[RENEWAL_DATE],
  [EFFECTIVE_DATE]: (state) => isValidDate(state[EFFECTIVE_DATE]),
  [GROUP_NUMBER]: (state) =>
    isValidGroupNumber(
      state[GROUP_NUMBER],
      state[CARRIER]?.name || state[NEW_CARRIER],
    ),
  [PREMIUM]: (state) => isValidAnnualPremium(state[PREMIUM]),
};

const mapOfCarrierPatterns = {
  [PRINCIPAL]: CARRIER_GROUP_NUMBER_VALIDATION_PATTERN.PRINCIPAL,
  [PRINCIPAL_FINANCIAL]: CARRIER_GROUP_NUMBER_VALIDATION_PATTERN.PRINCIPAL,
  [GUARDIAN]: CARRIER_GROUP_NUMBER_VALIDATION_PATTERN.GUARDIAN,
  ...Object.fromEntries(
    Object.values(ANTHEM_CARRIERS).map((c) => [
      c,
      CARRIER_GROUP_NUMBER_VALIDATION_PATTERN.ANTHEM,
    ]),
  ),
};

const isValidGroupNumber = (groupNumber, carrier) => {
  if (!carrier || !groupNumber) return true;

  const pattern = mapOfCarrierPatterns[carrier];
  return pattern ? pattern.test(groupNumber) : true;
};

const getGroupNumberValidationMessage = (carrier) => {
  if ([PRINCIPAL, PRINCIPAL_FINANCIAL].includes(carrier)) {
    return PRINCIPAL_VALIDATION;
  }
  if (Object.values(ANTHEM_CARRIERS).includes(carrier)) {
    return ANTHEM_VALIDATION;
  }
  if (carrier === GUARDIAN) {
    return GUARDIAN_VALIDATION;
  }
  return null;
};

const AddCurrentCoveragesModal = ({
  data,
  open,
  onClose,
  onAddCoverages,
  selectedCoverage,
  clientId,
}) => {
  const axiosInstance = useApi();
  const [submitCoverageState, setSubmitCoverageState] = useState({
    loading: false,
    error: null,
  });
  const [enableAddCarrier, setEnableAddCarrier] = useState(false);

  const [state, setState] = useState({
    ...modalInitValues,
    [COVERAGE]: selectedCoverage,
    validations: {
      [RENEWAL_DATE]: true,
      [EFFECTIVE_DATE]: true,
      [GROUP_NUMBER]: true,
    },
  });

  const handleChange = ({ value, name }) => {
    let newState = { ...state, [name]: value };
    if (validations[name]) {
      newState = {
        ...newState,
        validations: {
          ...state.validations,
          [name]: validations[name](newState),
        },
      };
    }
    if (name === CARRIER || name === NEW_CARRIER) {
      newState = {
        ...newState,
        validations: {
          ...state.validations,
          [GROUP_NUMBER]: validations[GROUP_NUMBER](newState),
        },
      };
    }
    if (name === EFFECTIVE_DATE) {
      newState = {
        ...newState,
        validations: {
          ...state.validations,
          [RENEWAL_DATE]: validations[RENEWAL_DATE](newState),
        },
      };
    }
    setState(newState);
  };

  const handleFilterOptions = (options, { inputValue }) =>
    options.filter(({ name }) => {
      const nameLC = name.toLowerCase();
      const inputValueLC = inputValue.toLowerCase();
      return nameLC.startsWith(inputValueLC);
    });

  const handleNumericFieldChange = ({ value, name }) => {
    const regex = /^\d+$/;
    if (value === "" || regex.test(value)) {
      handleChange({ value, name });
    }
  };

  const handleGroupNumberChange = ({ value, name }) => {
    const regex = /^[a-zA-Z0-9]*$/;
    if (regex.test(value)) {
      handleChange({ value, name });
    }
  };

  const handleSubtypeChange = ({ target }) => {
    const { value, name } = target;
    setState({ ...state, [name]: [value] });
  };

  const handleSubTypeSTD = ({ value, name }) => {
    setState({ ...state, [name]: value });
  };

  const preparePayloadCov = (cov, subtype) => {
    const obj = {
      ...cov,
      [PREMIUM]: parseInt(cov[PREMIUM], 10),
      clientId: parseInt(clientId, 10),
    };
    if (subtype) {
      obj.subtype = subtype;
    }
    return obj;
  };

  const cleanData = () => {
    const auxValues = { ...state };
    auxValues[CARRIER] = enableAddCarrier
      ? auxValues[NEW_CARRIER]
      : auxValues[CARRIER]?.name;
    auxValues[EFFECTIVE_DATE] = formatDate(auxValues[EFFECTIVE_DATE]);
    auxValues[RENEWAL_DATE] = formatDate(auxValues[RENEWAL_DATE]);
    delete auxValues[NEW_CARRIER];
    const subtypes = auxValues[SUBTYPES] ? [...auxValues[SUBTYPES]] : [];
    delete auxValues[SUBTYPES];
    if (subtypes?.length) {
      return subtypes.map((subtype) => {
        const subtypeValue =
          selectedCoverage === COVERAGE_NAMES.SDL
            ? (subtype?.value ?? "")
            : subtype;
        return preparePayloadCov(auxValues, subtypeValue);
      });
    }
    return [preparePayloadCov(auxValues)];
  };

  const handleClose = () => {
    if (!submitCoverageState.loading) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitCoverageState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));
      const coverages = cleanData();
      const response = await Promise.all(
        coverages.map(async (cov) => {
          const resp = await axiosInstance.post(
            "/brokerportal/v1/submitNewCoverage",
            cov,
          );
          submitEvent("Coverage Created", {
            client_id: cov.clientId,
            coverage_type: cov.coverage,
          });
          return resp;
        }),
      );
      const filteredResponse = response
        .filter((item) => item?.status === 200 && !!item?.data)
        .map((item) => item.data);
      onAddCoverages(normalizeClientProducts(filteredResponse));
      handleClose();
    } catch (err) {
      setSubmitCoverageState((prev) => ({
        ...prev,
        loading: false,
        error: extractErrorMessage(err),
      }));
    }
  };

  const disableSubmit = () => {
    const auxValues = { ...state };
    if (enableAddCarrier) {
      delete auxValues[CARRIER];
    } else {
      delete auxValues[NEW_CARRIER];
    }
    const cleanedData = cleanData();
    const data = cleanedData[0];
    const isIncompleteForm = Object.values({ ...data }).some(
      (s) => !s || s === undefined || s === "",
    );
    const isInvalidForm = Object.values({ ...state.validations }).some(
      (s) => !s || s === undefined,
    );
    const hasSubtype = [COVERAGE_NAMES.DENTAL, COVERAGE_NAMES.SDL].includes(
      data.coverage,
    )
      ? !data.subtype
      : false;
    return isIncompleteForm || isInvalidForm || hasSubtype;
  };

  const renderSubtype = () => {
    if (selectedCoverage === COVERAGE_NAMES.DENTAL) {
      return (
        <>
          <Grid size={12}>
            <Typography>Coverage Subtype</Typography>
          </Grid>
          <RadioGroup
            value={state[SUBTYPES]}
            row
            aria-labelledby="plan-type-label"
            name={SUBTYPES}
            onChange={handleSubtypeChange}
            data-testid="coverage-subtype"
          >
            <FormControlLabel value={PPO} control={<Radio />} label={PPO} />
            <FormControlLabel value={HMO} control={<Radio />} label={HMO} />
            <FormControlLabel
              value={INDEMNITY}
              control={<Radio />}
              label={INDEMNITY}
            />
          </RadioGroup>
        </>
      );
    } else if (selectedCoverage === COVERAGE_NAMES.SDL) {
      return (
        <>
          <Grid size={12}>
            <Typography>Coverage Subtype</Typography>
          </Grid>
          <Grid size={6}>
            <Autocomplete
              fullWidth
              clearOnBlur
              clearOnEscape
              onChange={(_, subtype, reason) =>
                ["clear", "selectOption"].includes(reason) &&
                handleSubTypeSTD({
                  value: subtype ? [subtype] : null,
                  name: SUBTYPES,
                })
              }
              options={STD_COV_SUBTYPES}
              isOptionEqualToValue={(option, value) =>
                option.name === value.name || option.value === value.value
              }
              getOptionLabel={({ name }) => name || ""}
              value={state[SUBTYPES]?.[0] ?? { name: "" }}
              filterOptions={handleFilterOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={"State Disability Plan"}
                  name={SUBTYPES}
                  required
                />
              )}
            />
          </Grid>
        </>
      );
    }
    return null;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md">
      <Grid component={Paper} container p={3}>
        <Grid size={12} mt={1} mb={5}>
          <Typography variant="header-large">{selectedCoverage}</Typography>
        </Grid>
        <Grid container gap={3}>
          {renderSubtype()}
          <Grid size={12}>
            <Typography>
              Provide the following information for the Coverage
            </Typography>
          </Grid>
          <Grid container spacing={2} size={12}>
            <Grid size={6}>
              {!enableAddCarrier ? (
                <Autocomplete
                  fullWidth
                  clearOnBlur
                  clearOnEscape
                  onChange={(_, carrier, reason) =>
                    reason === "selectOption" &&
                    handleChange({ value: carrier, name: CARRIER })
                  }
                  options={data[CARRIERS]}
                  getOptionLabel={({ name }) => name}
                  value={state[CARRIER]}
                  filterOptions={handleFilterOptions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={"Carrier"}
                      name={CARRIER}
                      required
                    />
                  )}
                />
              ) : (
                <TextField
                  fullWidth
                  label="Enter carrier name"
                  variant="outlined"
                  required
                  onChange={({ target }) => handleChange(target)}
                  value={state[NEW_CARRIER]}
                  name={NEW_CARRIER}
                  autoComplete="off"
                  slotProps={{
                    htmlInput: { "data-testid": "coverage-new-carrier" },
                  }}
                />
              )}
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                value={state[GROUP_NUMBER]}
                onChange={({ target }) => handleGroupNumberChange(target)}
                name={GROUP_NUMBER}
                label="Group Number"
                autoComplete="off"
                required
                slotProps={{
                  htmlInput: { "data-testid": "coverage-group-number" },
                }}
                error={
                  !!state[GROUP_NUMBER] && !state.validations[GROUP_NUMBER]
                }
                helperText={
                  state[GROUP_NUMBER] &&
                  !state.validations[GROUP_NUMBER] &&
                  getGroupNumberValidationMessage(
                    state[CARRIER]?.name || state[NEW_CARRIER],
                  )
                }
              />
            </Grid>
          </Grid>
          <Grid size={12}>
            {enableAddCarrier ? (
              <Button
                onClick={() => {
                  state[NEW_CARRIER] = null;
                  setEnableAddCarrier(false);
                  state.validations[GROUP_NUMBER] = true;
                }}
                variant="tertiary"
              >
                Search Carrier
              </Button>
            ) : (
              <Button
                data-testid="coverage-add-carrier-toggle"
                onClick={() => {
                  state[CARRIER] = null;
                  setEnableAddCarrier(true);
                  state.validations[GROUP_NUMBER] = true;
                }}
                variant="tertiary"
              >
                I do not see my carrier
              </Button>
            )}
          </Grid>
          <Grid container spacing={2} size={12}>
            <Grid size={6}>
              <InputDate
                label={"Effective Date"}
                required
                value={state[EFFECTIVE_DATE]}
                name={EFFECTIVE_DATE}
                error={
                  state[EFFECTIVE_DATE] && !state.validations[EFFECTIVE_DATE]
                }
                textFieldParams={{
                  fullWidth: true,
                  helperText:
                    state[EFFECTIVE_DATE] &&
                    !state.validations[EFFECTIVE_DATE] &&
                    "Invalid Date",
                }}
                onChange={(value) =>
                  handleChange({ value, name: EFFECTIVE_DATE })
                }
              />
            </Grid>
            <Grid size={6}>
              <InputDate
                label="Renewal Date"
                required
                value={state[RENEWAL_DATE]}
                name={RENEWAL_DATE}
                error={state[RENEWAL_DATE] && !state.validations[RENEWAL_DATE]}
                textFieldParams={{
                  fullWidth: true,
                  helperText:
                    state[RENEWAL_DATE] &&
                    !state.validations[RENEWAL_DATE] &&
                    "Invalid Date",
                }}
                onChange={(value) =>
                  handleChange({ value, name: RENEWAL_DATE })
                }
                shouldDisableDate={(date) =>
                  state[EFFECTIVE_DATE] && state[EFFECTIVE_DATE] >= date
                }
                disabled={!state[EFFECTIVE_DATE]}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                value={state[PREMIUM]}
                onChange={({ target }) => handleNumericFieldChange(target)}
                name={PREMIUM}
                label="Annualized Premium"
                autoComplete="off"
                required
                slotProps={{
                  htmlInput: { "data-testid": "coverage-premium" },
                }}
                error={!!state[PREMIUM] && !state.validations[PREMIUM]}
                helperText={
                  state[PREMIUM] &&
                  !state.validations[PREMIUM] &&
                  PREMIUM_VALIDATION
                }
              />
            </Grid>
          </Grid>
          <Grid container gap={3} size={12}>
            <Button
              variant="tertiary"
              onClick={handleClose}
              sx={{ ml: "auto" }}
            >
              cancel
            </Button>
            <Button
              data-testid="coverage-submit"
              variant="primary"
              onClick={handleSubmit}
              disabled={disableSubmit()}
              loading={submitCoverageState.loading}
            >
              ADD COVERAGE
            </Button>
          </Grid>
          {submitCoverageState.error ? (
            <Grid container size={12} justifyContent="end">
              <Box my={1}>
                <Alert severity="error">{submitCoverageState.error}</Alert>
              </Box>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default AddCurrentCoveragesModal;
